import { memo, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ShellIcon } from "./ShellIcons";
import { OwnerPick } from "../owners";
import { api } from "../api/client";
import AvatarPic from "./AvatarPic";
import type { LeadDetail as Lead, LeadRow, Me, Message, OrderBrief, ShopOrder } from "../api/types";
import { useClock, useMirrored } from "../hooks";
import { useHold } from "../fresh";
import { useBackLayer } from "../backstack";
import { can } from "../util";
import { BsMessenger } from "react-icons/bs";
import { adShort, avatarStyle, initialOf, isMessenger, originLabel, phoneLabel } from "../ui";
import SaleDialog, { guessAmount, looksPaid } from "./SaleDialog";
import Transcript from "./Transcript";
import VoiceComposer from "./VoiceComposer";
import { adLabel, sinceWord } from "../customers";
import { progressLabel } from "../progress";
import { addChatNote } from "../chatNotes";
import { SegInd, stillWanted, toast } from "./Kit";
import { AdPic, adFilled } from "./CustomerBits";
import DealerTag from "./DealerTag";
import { NetDot } from "../labels";
import CODCard from "./CODCard";
import { PriceDrawer } from "./PriceBoard";
import { isSingaporeNumber } from "../prices";
import { lazyView, ViewShell } from "../views";
import { PageShown } from "../pageShown";

/*
 * 客户详情 is the customer card 客户表's drawer draws too (CustomerCard), fetched on its own: it brings 客户表's
 * parts and their styles, which load after the console's own as they do on 客户表. The chat list fetches it
 * ahead (preloadCard), so the column is there when the first chat opens.
 */
const loadCard = () => import("./CustomerCard");
const CustomerCard = lazyView(loadCard);
export const preloadCard = () => void loadCard().catch(() => undefined);
/* 用草稿重跑 on one of her replies, fetched when it is first pressed. */
const loadReplay = () => import("./ReplayChat");
const ReplayChat = lazyView(loadReplay);

interface Props {
  lead: Lead;
  /** The same customer's row in the list: who set the progress, which campaign. */
  row?: LeadRow;
  me: Me | null;
  paneRef: React.RefObject<HTMLDivElement | null>;
  /** Re-fetch this lead and the list after a write. */
  onChanged: () => void;
  /** Mobile: go back to the list pane. */
  onBack: () => void;
  /** Open another conversation: the same person's other number. */
  onOpen?: (waID: string) => void;
  /** Room for 客户详情 as a column beside the chat (LeadsView measures it). */
  col?: boolean;
  /** That column unfolded: remembered across chats and visits. */
  paneOn?: boolean;
  onPane?: (on: boolean) => void;
  /** Opened from 跟进: 「‹ 回到跟进」 under the header. */
  onBackTo?: () => void;
}

/** An order as the shop's list under this number has it, in the customer table's shape. */
const brief = (o: ShopOrder): OrderBrief => ({
  order_no: o.order_no,
  payment_status: o.payment_status,
  fulfillment_status: o.fulfillment_status,
  total: o.total,
  refunded: o.refunded,
  items: (o.items ?? []).map((i) => `${i.name}×${i.quantity}`).join("、"),
  payment_method: o.payment_method,
  payment_reference: o.payment_reference,
  courier: o.courier,
  tracking_no: o.tracking_no,
  ordered_at: o.created_at,
  paid_at: o.paid_at,
  shipped_at: o.shipped_at,
  delivered_at: o.delivered_at,
});

/** Why 小爱 asked for a colleague, in plain words, when the chat's last note says it; otherwise nothing. */
function handoffWhy(note?: string): string {
  if (!note) return "";
  if (/没有一步对得上.*(转人工|交给营养师)/.test(note)) return "客户问的不在剧本里";
  if (/打不开客户发的/.test(note)) return "小爱看不懂客户发来的东西";
  if (/发不出去|回不了这条消息/.test(note)) return "小爱的回复发不出去";
  if (/全部关掉/.test(note)) return "自动回复全部关掉了";
  return "";
}

/** 客户详情 while its file is on the way (the first chat only): the column's shape, quiet. */
function CardWait() {
  return (
    <div className="wc-cardwait" aria-hidden="true">
      <i className="pskel-b" />
      <i className="pskel-b" />
      <i className="pskel-b" />
    </div>
  );
}

/**
 * 客户详情 that plays its way out before it goes (prototype chat.css .chat-pane, v2.20): `on` while it is drawn, `out`
 * for the 200 ms it slides off. The chat keeps its column until then, so the grid changes once, after the slide.
 */
function useLeave(open: boolean, ms = 200): [on: boolean, out: boolean] {
  const [on, setOn] = useState(open);
  const [was, setWas] = useState(open);
  if (was !== open) {
    setWas(open);
    if (open) setOn(true);
  }
  useEffect(() => {
    if (open || !on) return;
    if (stillWanted()) {
      setOn(false);
      return;
    }
    const t = window.setTimeout(() => setOn(false), ms);
    return () => window.clearTimeout(t);
  }, [open, on, ms]);
  return [on, on && !open];
}

/** What comes in again each time 对话 is shown (prototype: the page is drawn anew on each visit): the bars over the chat
 * rise in (chatin, .35 s), the 负责 chip and the labels pop (pop, .35 s / .3 s, the press spring). */
const ALERT_IN: Keyframe[] = [{ transform: "translateY(10px) scale(.98)", opacity: 0 }, { transform: "none", opacity: 1 }];
const POP_IN: Keyframe[] = [{ transform: "scale(.6)", opacity: 0 }, { transform: "none", opacity: 1 }];
const SPRING = "cubic-bezier(.34, 1.56, .64, 1)";

/**
 * The shop's orders under a number, as last read. Going back to a chat within half a minute shows them without a new
 * read (as the API keeps its reports, client.ts KEEP_MS), and a read already on its way is shared: 客户详情 is a column
 * now, open from chat to chat, where production read the orders only when its drawer was opened.
 */
const ORDERS_KEEP_MS = 30_000;
const ordersRead = new Map<string, { at: number; v: ShopOrder[] | "off" }>();
const ordersOnWay = new Map<string, Promise<ShopOrder[] | "off">>();
function readOrders(waID: string, fresh = false): Promise<ShopOrder[] | "off"> {
  const had = ordersRead.get(waID);
  if (!fresh && had && Date.now() - had.at < ORDERS_KEEP_MS) return Promise.resolve(had.v);
  const on = ordersOnWay.get(waID);
  if (on && !fresh) return on;
  const p = api
    .leadOrders(waID)
    .then(
      (r) => r.orders ?? [],
      () => "off" as const,
    )
    .then((v) => {
      ordersRead.delete(waID);
      ordersRead.set(waID, { at: Date.now(), v });
      while (ordersRead.size > 40) ordersRead.delete(ordersRead.keys().next().value!);
      return v;
    })
    .finally(() => ordersOnWay.delete(waID));
  ordersOnWay.set(waID, p);
  return p;
}

/**
 * 客户详情's orders, read while it is open and 对话 is on screen — not for the chat LeadsView opens before the page is
 * first shown. A child of its own, so the page shown or hidden draws this and not the whole chat again.
 */
function ReadOrders({ waID, on, onRead }: { waID: string; on: boolean; onRead: (v: ShopOrder[] | "off") => void }) {
  const shown = useContext(PageShown);
  useEffect(() => {
    if (!on || !shown) return;
    let live = true;
    void readOrders(waID).then((v) => live && onRead(v));
    return () => {
      live = false;
    };
  }, [on, shown, waID, onRead]);
  return null;
}

/**
 * 对话 shown again: it was kept laid out off screen, so nothing it drew plays by itself (Kit usePlay's note). A child
 * of its own, so a page shown or hidden draws this and not the whole chat again.
 */
function ComeBack({ root }: { root: React.RefObject<HTMLDivElement | null> }) {
  const shown = useContext(PageShown);
  const was = useRef(shown);
  useLayoutEffect(() => {
    const back = shown && !was.current;
    was.current = shown;
    const el = root.current;
    if (!back || !el || stillWanted()) return;
    for (const a of el.querySelectorAll<HTMLElement>(".wc-alert")) a.animate(ALERT_IN, { duration: 350, easing: "cubic-bezier(.2, .8, .2, 1)" });
    el.querySelector<HTMLElement>(".wc-head .own-btn.set")?.animate(POP_IN, { duration: 350, easing: SPRING });
    for (const t of el.querySelectorAll<HTMLElement>(".cc.at-chat .wc-tag")) t.animate(POP_IN, { duration: 300, easing: SPRING });
  }, [shown, root]);
  return null;
}

/** 「等了 7 小时」 on the 要真人 bar (prototype .chat-alert-w): how long the customer has waited, moving with the clock. */
function Waited({ since }: { since?: string }) {
  useClock();
  const w = sinceWord(since);
  return w ? <span className="wc-alert-w">等了 {w}</span> : null;
}

/**
 * One conversation, as the WhatsApp chat pane: a 59px header naming the
 * customer, the thread on the wallpaper, the composer at the bottom.
 *
 * The header holds the one decision an agent makes all day — does the bot
 * answer this person, or do I — as two labelled buttons. Everything else sits
 * behind 详情: the customer card (CustomerCard), what to do now first, then what
 * the customer said and bought, then what a colleague changes.
 */
function LeadDetail({ lead, row, me, paneRef, onChanged, onBack, onOpen, col = false, paneOn = true, onPane, onBackTo }: Props) {
  const mayEdit = can(me, "edit_conversation");
  const maySpeak = can(me, "send_voice");
  const mayWrite = can(me, "send_text");
  // 用草稿重跑: whoever edits her words reads them on the chat they are for (the call reads chats too).
  const mayReplay = can(me, "edit_ai") && can(me, "view_transcript");
  const [replaying, setReplaying] = useState<Message | null>(null);
  const main = useRef<HTMLDivElement>(null);
  // 客户详情 over the chat (a narrower screen, a phone); on a wide one it is the column (paneOn).
  const [drawer, setDrawer] = useState(false);
  // 回复: the line the next message quotes (owner, 2026-10-20: 「whatsapp是有reply功能就是针对他问的那个问题回复的」).
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  // 价钱: the price list in a drawer over the page, as the prototype opens it (chat.js prices → openDrawer); 客户详情
  // stays where it was under it.
  const [prices, setPrices] = useState(false);
  // A price pressed in 价钱, on its way into the reply box.
  const [fill, setFill] = useState<{ text: string; seq: number } | null>(null);
  const details = col ? paneOn : drawer;
  // On its way out it keeps its place a moment (useLeave).
  const [card, cardOut] = useLeave(details, col ? 200 : 300);
  // The column beside the chat.
  const side = col && card;
  // The column slides in when it is opened here — not each time the next chat opens with it already open.
  const [slide, setSlide] = useState(false);
  // Over the chat (a narrower computer): a shade beside it, and Esc, close it too (prototype .chat-ovscrim).
  useEffect(() => {
    if (col || !drawer) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // A field being typed in, a menu or a question over it hears Esc first.
      if (e.target instanceof Element && e.target.closest("input, textarea, select, [contenteditable]")) return;
      if (document.querySelector(".wc-modal, .lz-drawer, .cr-flt, .own-menu")) return;
      setDrawer(false);
    };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [col, drawer]);
  function toggleDetails() {
    setSlide(true);
    if (!col) setDrawer((v) => !v);
    else onPane?.(!paneOn);
  }
  // Esc closes 价钱, as the prototype's drawers close — unless a menu or a question over it hears it first.
  useEffect(() => {
    if (!prices) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector(".wc-modal, .pl-menu")) return;
      setPrices(false);
    };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [prices]);
  // The face and the name: 客户详情, opened if it is not.
  function openDetails() {
    if (!details) toggleDetails();
  }
  // The header's ad opens 客户详情 at 广告足迹, which flashes (prototype chat.js showJourney); the ad itself is a
  // press away there (看广告). Read once, on the press.
  const jump = useRef(0);
  useEffect(() => () => window.clearTimeout(jump.current), []);
  function showJourney() {
    const was = details;
    if (!was) {
      setSlide(true);
      if (col) onPane?.(true);
      else setDrawer(true);
    }
    window.clearTimeout(jump.current);
    jump.current = window.setTimeout(
      () => {
        const box = main.current?.querySelector<HTMLElement>(".wc-drawer-b");
        const s = box?.querySelector<HTMLElement>('[data-sec="jy"]');
        if (!box || !s) return;
        const head = box.querySelector<HTMLElement>(".cc-head")?.offsetHeight ?? 0;
        const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        box.scrollTo({ top: Math.max(0, s.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - head), behavior: still ? "auto" : "smooth" });
        s.classList.remove("cc-flash");
        void s.offsetWidth;
        s.classList.add("cc-flash");
      },
      was ? 0 : 460,
    );
  }
  const [botOn, setBotOn] = useMirrored(lead.bot_enabled);
  // What the card shows of the customer and changes here: on screen at once, the server's again if a write fails.
  const [progress, setProgress] = useMirrored<string>(lead.status || "new");
  const [progressBy, setProgressBy] = useMirrored<string>(row?.status_by ?? "");
  const [note, setNote] = useMirrored(lead.note ?? row?.note ?? "");
  const [origin, setOrigin] = useMirrored(lead.origin ?? row?.origin ?? "");
  const [originChosen, setOriginChosen] = useMirrored(!!(lead.origin_chosen ?? row?.origin_chosen));
  const [campaign, setCampaign] = useMirrored(row?.ad_campaign ?? "");
  const [noted, setNoted] = useMirrored(!!row?.ad_noted);
  const [thumb, setThumb] = useMirrored(row?.ad_thumb ?? "");
  // The shop's orders under this number: as last read, if they were (ReadOrders reads them again when that is old). null
  // while loading; "off" when the server cannot read the shop (an older API), which hides the section.
  const [orders, setOrders] = useState<ShopOrder[] | null | "off">(() => ordersRead.get(lead.wa_id)?.v ?? null);
  // The drawer open: somebody is in the middle of this customer, and the tab
  // does not reload itself under them (fresh.tsx). The column is not: it
  // stays open from chat to chat, and comes back open after a reload.
  useHold(drawer || prices);
  // On a phone the details cover the chat: the phone's back closes them.
  useBackLayer(drawer, () => setDrawer(false));
  useBackLayer(prices, () => setPrices(false));

  // After a number is attached or taken off: its orders join or leave, read afresh.
  function reloadOrders() {
    void readOrders(lead.wa_id, true).then((v) => v !== "off" && setOrders(v));
    onChanged();
  }

  // Who this is, in the console's words for them: the feedback after each change names them (prototype chat.js).
  const who = lead.name || (isMessenger(lead) ? "Messenger 客户" : phoneLabel(lead.wa_id));
  const botName = lead.effective_engine === "playbook" ? "剧本" : "小爱";

  // Said at the top and as a line in the chat (chatNotes.ts) once the server has it (prototype chat.js setReply).
  function sayWho(bot: boolean) {
    toast(bot ? `${who} 交回${botName}` : `你接手了 ${who}，${botName}先不说话`);
    addChatNote(lead.wa_id, bot ? (botName === "小爱" ? "交回小爱，她接着回" : "交回剧本，剧本接着回") : `${me?.name || "同事"} 接手了，${botName}先不说话`);
  }

  async function toggleBot(next: boolean) {
    setBotOn(next);
    try {
      await api.setBot(lead.wa_id, next);
      sayWho(next);
      onChanged();
    } catch {
      setBotOn(undefined); // fall back to server truth
      toast("没换成，再按一次", "bad");
    }
  }

  // 要真人 done by hand: the switch stays where it is, and saying so takes the flag down.
  // 人工 for one 小爱 never answers (ai_off): the server refuses 小爱回复 there.
  async function handled() {
    try {
      await api.setBot(lead.wa_id, lead.ai_off ? false : (botOn ?? lead.bot_enabled));
      toast(`好了，${who} 不用再等`, "win");
      onChanged();
    } catch {
      /* the banner stays; pressing again tries again */
    }
  }

  // This one customer to 小爱, from a WhatsApp line that answers only its
  // list: the conversation is set to 小爱 and its bot switched on, which lets
  // them through the list — nobody else (owner, 2026-09-25).
  async function handToXiaoai() {
    setBotOn(true);
    try {
      await api.setEngine(lead.wa_id, "ai");
      await api.setBot(lead.wa_id, true);
      toast(`${who} 交回小爱`);
      addChatNote(lead.wa_id, "交回小爱，她接着回");
      onChanged();
    } catch {
      setBotOn(undefined);
      toast("没换成，再按一次", "bad");
    }
  }

  // A colleague's word wins over the rules: the server records it as theirs,
  // and the no-reply rule leaves it alone.
  async function changeProgress(next: string) {
    setProgress(next);
    setProgressBy("human");
    try {
      await api.setStatus(lead.wa_id, next);
      toast(`${who} → ${progressLabel(next)}`);
      onChanged();
    } catch {
      setProgress(undefined);
      setProgressBy(undefined);
      toast("进度没改到，再选一次", "bad");
    }
  }

  // A note that did not save throws: the card keeps the words and asks again.
  async function saveNote(next: string) {
    await api.setNote(lead.wa_id, next);
    setNote(next);
    toast(next ? "Remark 存好了" : "Remark 拿掉了");
    onChanged();
  }

  // 来源: "" goes back to the click's answer, which the server says on the next read.
  async function changeOrigin(next: string) {
    setOrigin(next);
    setOriginChosen(next !== "");
    try {
      await api.setOrigin(lead.wa_id, next);
      toast(next ? `${who} 来源：${originLabel(next)}` : `${who} 的来源改回自动`);
      onChanged();
    } catch {
      setOrigin(undefined);
      setOriginChosen(undefined);
      toast("来源没改到，再选一次", "bad");
    }
  }

  // The campaign CS saw on the phone; "" takes it off.
  async function changeCampaign(next: string, pic?: string) {
    setCampaign(next);
    setNoted(next !== "");
    setThumb(next ? (pic ?? "") : "");
    try {
      await api.noteCampaign(lead.wa_id, next);
      toast(next ? `${who} 点的广告：${adLabel(next)}` : `${who} 的广告拿掉了`);
      onChanged();
    } catch {
      setCampaign(undefined);
      setNoted(undefined);
      setThumb(undefined);
    }
  }

  // 现在要做's 去回复: to the box the reply is typed in, from over the chat too.
  function reply() {
    if (!col) setDrawer(false);
    main.current?.querySelector<HTMLTextAreaElement>(".wc-foot textarea")?.focus();
  }

  const messages = lead.messages ?? [];
  const conversions = lead.conversions ?? [];
  const maySell = can(me, "record_sale") && !me?.record_sale_off;
  const [sale, setSale] = useState(false);
  // What the server said about a sale that was recorded but not reported. Shown
  // once, here, rather than swallowed.
  const [saleNote, setSaleNote] = useState("");
  const paidLooks = looksPaid(messages);
  const paidAmount = paidLooks ? guessAmount(messages) : "";
  // Since the customer last wrote: how long a 要真人 has waited.
  const waitFrom = row?.last_in_at ?? [...messages].reverse().find((m) => m.direction === "in")?.at;
  const purchased = conversions.some((c) => c.event === "Purchase");
  // Who answers when the bot is on, in the team's words.
  const botWord = lead.effective_engine === "playbook" ? "剧本回复" : lead.effective_engine === "ai" ? "小爱回复" : "自动回复";
  // Nobody answers this customer automatically, whatever the switch says: the
  // line only replies to its trial list (none), or the shop pulled the plug
  // (human). A switch here would promise a reply that never comes — except
  // 小爱回复 for a customer the list shuts out, which hands them over.
  // A dealer, a dealer's customer or progress 代理 (ai_off) is nobody automatic's
  // either (owner, 2026-10-20: 「小爱自动回复不回代理和代理的顾客」).
  const nobody = lead.effective_engine === "none" || lead.effective_engine === "human" || !!lead.ai_off;
  const shutOut = lead.effective_engine === "none";
  // With no ad kept and none noted, the one the first line points to, said to be a guess.
  const guessed = !row?.ad_campaign && !lead.ad_id && !!row?.ad_guess;
  // WhatsApp withheld their first line and the server worked the ad out (系统推断): 客户表 showed it
  // and the chat's own header said nothing (owner 10/12: 「系统判断的 对话不会显示是什么广告吗？」).
  const inferred = !row?.ad_campaign && !lead.ad_id && !guessed && !!row && adFilled(row);
  const adLine = row?.ad_campaign
    ? adLabel(row.ad_campaign)
    : lead.ad_id
      ? adShort(lead.ad_headline, lead.ad_id, lead.ad_network)
      : guessed && row?.ad_guess
        ? `推断：${row.ad_guess.startsWith("「") ? row.ad_guess : adLabel(row.ad_guess)}`
        : inferred && row?.ad_inferred_campaign
          ? adLabel(row.ad_inferred_campaign)
          : "";
  const adThumb = row?.ad_thumb || (inferred ? row?.ad_inferred_thumb : undefined);
  // How many ads they have tapped, said on the card (prototype 「点的广告 · 共 2 条」).
  const taps = row?.ads_tapped ?? 0;
  const adTitle =
    (row?.ad_campaign || lead.ad_headline || adLine) +
    (row?.ad_noted ? "\n客服记的广告系列" : "") +
    (guessed ? "\n9/15 以前没记下是哪一条广告，这是看第一句话推断的" : "") +
    (inferred ? "\nWhatsApp 没把这个客户的第一句送来；系统按 Meta 每条广告当天开始的对话数认出是这条" : "") +
    (taps > 1 ? `\n一共点过 ${taps} 条广告` : "") +
    "\n按一下看广告足迹";

  // The customer as the card reads them: the list's row, or — with another list on screen — what the chat
  // says of them; and what was just changed here, before the next read brings it back.
  const base: LeadRow = row ?? {
    wa_id: lead.wa_id,
    network: lead.network,
    name: lead.name,
    avatar_url: lead.avatar_url,
    bot_enabled: lead.bot_enabled,
    engine: lead.engine,
    product: lead.product,
    needs_human: lead.needs_human,
    status: lead.status,
    internal: lead.internal,
    lead_stage: lead.lead_stage,
    lead_stage_by: lead.lead_stage_by,
    ad_id: lead.ad_id,
    ad_headline: lead.ad_headline,
    ad_link: lead.ad_source_url,
    ad_clicked_at: lead.ad_clicked_at,
    labels: lead.labels,
    first_seen_at: lead.first_seen_at,
    legacy_dealer: lead.legacy_dealer,
    legacy_customer: lead.legacy_customer,
    ai_off: lead.ai_off,
  };
  const cardRow: LeadRow = {
    ...base,
    status: progress,
    status_by: progressBy,
    note,
    origin: origin || undefined,
    origin_chosen: originChosen,
    ad_campaign: campaign || undefined,
    ad_noted: noted,
    ad_thumb: thumb || undefined,
  };

  return (
    <div ref={main} className={`wc-main${side ? " col" : ""}`}>
      <ComeBack root={main} />
      <ReadOrders waID={lead.wa_id} on={details} onRead={setOrders} />
      <div className="wc-head">
        <button type="button" className="wc-ico wc-back" onClick={onBack} title="回到列表">
          <ShellIcon name="left" size={22} />
        </button>
        {/* The face and the name open 客户详情, as in WhatsApp (prototype .chat-who-av, .chat-who-n). */}
        <button type="button" className="wc-av wc-who-av" style={avatarStyle(lead.wa_id)} onClick={openDetails} title="看客户详情" aria-label="看客户详情">
          <AvatarPic url={lead.avatar_url} />
          {initialOf(lead.name || lead.wa_id)}
          <NetDot fb={isMessenger(lead)} />
        </button>
        <div className="wc-head-t">
          <b className="wc-head-n">
            <button type="button" className="wc-name-t wc-who-n" onClick={openDetails} title="看客户详情">
              {lead.name || (isMessenger(lead) ? "Messenger 客户" : phoneLabel(lead.wa_id))}
            </button>
            <DealerTag d={lead.legacy_dealer} c={lead.legacy_customer} fit />
            {/* 负责的同事, picked here (owners.tsx): the phone's JH / MAD. A
                phone's header has no room for it (the name went to 0 px), so
                there it is in 客户详情 › 更多. */}
            <OwnerPick waID={lead.wa_id} compact who={who} />
          </b>
          <span>
            {/* A Messenger id means nothing to a person; the network does. */}
            {isMessenger(lead) ? (
              <>
                <BsMessenger className="wc-net" /> Messenger
              </>
            ) : (
              phoneLabel(lead.wa_id)
            )}
            {/* The ad on this line when the header has no room for its card (prototype .chat-who-ad). */}
            {adLine && (
              <button type="button" className="wc-who-ad" title={adTitle} onClick={showJourney}>
                {adThumb && <AdPic src={adThumb} size={16} />}
                <span>{adLine}</span>
                {inferred && <small className="wc-adinf">自动猜的</small>}
              </button>
            )}
          </span>
        </div>
        {/* The ad they tapped (prototype .chat-adcard): opens 客户详情 at 广告足迹. */}
        {adLine && (
          <button type="button" className="wc-adcard" title={adTitle} onClick={showJourney}>
            <AdPic src={adThumb} size={34} />
            <span className="wc-adcard-t">
              <small>
                {guessed ? "看第一句推断的广告" : "点的广告"}
                {!guessed && taps > 1 && (
                  <>
                    {" · "}
                    <b>共 {taps} 条</b>
                  </>
                )}
                {inferred && <small className="wc-adinf">自动猜的</small>}
              </small>
              <b>{guessed ? adLine.replace(/^推断：/, "") : adLine}</b>
            </span>
            <ShellIcon name="right" size={15} className="wc-adcard-go" />
          </button>
        )}
        <span className="wc-head-sp" />
        {shutOut && mayEdit ? (
          <div className="wc-reply-mode" role="radiogroup" aria-label="谁来回">
            <button
              type="button"
              role="radio"
              aria-checked={false}
              title="只把这位客户交给小爱，其他客户照旧人工"
              onClick={() => void handToXiaoai()}
            >
              <ShellIcon name="sparkles" /> <Mode w="小爱回复" />
            </button>
            <button type="button" role="radio" aria-checked className="on human" title={lead.engine_why || "现在小爱不回这位客户"}>
              <ShellIcon name="headset" /> <Mode w="人工回复" />
            </button>
          </div>
        ) : nobody ? (
          <span className="wc-reply-ro manual" title={lead.ai_off || lead.engine_why || "现在小爱不回这位客户"}>
            <ShellIcon name="headset" /> <Mode w="人工回复" />
            {lead.ai_off && <small>代理</small>}
          </span>
        ) : mayEdit ? (
          <div className={`wc-reply-mode${botOn ? " is-ai" : ""}`} role="radiogroup" aria-label="谁来回">
            <SegInd />
            <button
              type="button"
              role="radio"
              aria-checked={botOn}
              className={botOn ? "on bot" : ""}
              title="小爱自动回这位客户"
              onClick={() => !botOn && void toggleBot(true)}
            >
              <ShellIcon name="sparkles" /> <Mode w={botWord} />
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!botOn}
              className={!botOn ? "on human" : ""}
              title="小爱不回，由你来回"
              onClick={() => botOn && void toggleBot(false)}
            >
              <ShellIcon name="headset" /> <Mode w="人工回复" />
            </button>
          </div>
        ) : (
          <span className="wc-reply-ro">
            <Mode w={botOn ? botWord : "人工回复"} />
          </span>
        )}
        <button
          type="button"
          className={`wc-detail-btn price${prices ? " on" : ""}`}
          onClick={() => setPrices((v) => !v)}
          aria-expanded={prices}
          title="价目表"
        >
          <ShellIcon name="tag" /> 价钱
        </button>
        {/* 客户详情: the column beside the chat folds and unfolds (a wide
            screen), or the details come over the chat (narrower, a phone). */}
        <button
          type="button"
          className={`wc-detail-btn icon wc-tip${details ? " on" : ""}`}
          data-tip={details ? "收起客户详情" : "打开客户详情"}
          aria-label={details ? "收起客户详情" : "打开客户详情"}
          aria-pressed={details}
          onClick={toggleDetails}
        >
          <ShellIcon name={details ? "paneclose" : "paneopen"} className="wc-ic-wide" />
          <ShellIcon name="info" className="wc-ic-phone" />
        </button>
      </div>

      {onBackTo && (
        <div className="wc-fuback">
          <button type="button" className="wc-fuback-b" onClick={onBackTo}>
            <ShellIcon name="left" size={16} />
            <b>回到跟进</b>
          </button>
        </div>
      )}

      {/* The alerts in one block under the header, one under the other, out of
          the thread's scroller (prototype v2.20): each used to stick at the top
          on its own, and two drew over each other. */}
      <div className="wc-alerts">
        {/* Somebody is needed here: the loudest thing in the pane, in the words every page uses (要真人 = 小爱请同事
            接手). last_decision is the system's latest note on the chat, not always why the flag went up: printed
            whole it read as a log (「没有一步对得上（上次到「sleep」），交给营养师，机器人停下」). A note that does say
            why is put in plain words; the note itself is behind ⓘ. */}
        {lead.needs_human && (
          <div className="wc-alert">
            <ShellIcon name="hand" />
            <span>
              <b>这条对话在等真人接手</b>
              {handoffWhy(lead.last_decision) && <em className="wc-alert-why"> · {handoffWhy(lead.last_decision)}</em>}
              {lead.last_decision && (
                <span className="wc-alert-i" title={lead.last_decision} aria-label={lead.last_decision}>
                  <ShellIcon name="info" size={14} />
                </span>
              )}
            </span>
            <Waited since={waitFrom} />
            {/* Done by hand — an order cancelled in cp, a question answered on the phone — and the
                flag stayed up, because only flipping 小爱回复 / 人工回复 took it down. The same call
                with the switch where it is clears it and changes nothing else. */}
            {mayEdit && (
              <button type="button" className="quick" onClick={() => void handled()}>
                <ShellIcon name="check" size={15} />
                处理好了
              </button>
            )}
          </div>
        )}
        {/* A cash-on-delivery order the customer confirmed, for a colleague to place in cp. */}
        {lead.cod_request && !lead.transcript_hidden && (
          <CODCard waID={lead.wa_id} cod={lead.cod_request} mayClose={mayEdit} onClosed={onChanged} />
        )}
        {/* Paid in the chat, not in the books. This stays until the sale is recorded. */}
        {paidLooks && !purchased && !lead.internal && maySell && (
          <div className="wc-alert pay">
            <ShellIcon name="coins" />
            <span>
              <b>客户像是付款了，还没记成交</b>
              {/* The last RM figure in the chat, as 记一笔 will fill it in (prototype .chat-alert-1 · RM584). */}
              {paidAmount && (
                <>
                  {" · "}
                  <b className="num">RM{paidAmount}</b>
                </>
              )}
            </span>
            <button type="button" className="quick primary" onClick={() => setSale(true)}>
              <ShellIcon name="coins" size={15} />
              记一笔
            </button>
          </div>
        )}
        {saleNote && (
          <div className="wc-alert note">
            <ShellIcon name="info" />
            <span>{saleNote}</span>
            <button type="button" className="quick" onClick={() => setSaleNote("")}>
              知道了
            </button>
          </div>
        )}
      </div>

      {/* The scroller. It exists so the composer can sit OUTSIDE it: an input
          box you have to scroll to the bottom of a conversation to reach is one
          an agent fights on every reply. */}
      <div className="wc-body" ref={paneRef}>
        {lead.transcript_hidden ? (
          <div className="wc-sys">你的角色看不到对话内容</div>
        ) : (
          <Transcript
            messages={messages}
            paneRef={paneRef}
            peer={{ waID: lead.wa_id, name: lead.name || "", avatarURL: lead.avatar_url || "" }}
            onReply={mayWrite ? setReplyTo : undefined}
            onReplay={mayReplay ? setReplaying : undefined}
            ad={
              adLine && !guessed && !inferred
                ? {
                    name: adLine,
                    sub: lead.ad_headline && lead.ad_headline !== adLine ? lead.ad_headline : undefined,
                    thumb: adThumb,
                    at: lead.ad_clicked_at || undefined,
                  }
                : undefined
            }
          />
        )}
      </div>

      {(maySpeak || mayWrite) && !lead.transcript_hidden && (
        <VoiceComposer
          waID={lead.wa_id}
          onSent={onChanged}
          canText={mayWrite}
          canVoice={maySpeak}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
          peerName={lead.name || ""}
          botName={botOn && !nobody ? botName : undefined}
          fill={fill}
        />
      )}

      {sale && (
        <SaleDialog
          waID={lead.wa_id}
          who={who}
          messages={messages}
          onDone={onChanged}
          onClose={(note) => {
            setSale(false);
            if (note) setSaleNote(note);
          }}
        />
      )}

      {/* Over the whole page, the page dimmed and a little blurred behind it; a press there closes it (prototype Drawer). */}
      {replaying?.at && (
        <ViewShell wait={null}>
          <ReplayChat key={replaying.at} waID={lead.wa_id} at={replaying.at} onClose={() => setReplaying(null)} />
        </ViewShell>
      )}
      {prices && <div className="wc-plscrim" onClick={() => setPrices(false)} aria-hidden="true" />}
      {prices && (
        <PriceDrawer
          product={lead.product ?? ""}
          singapore={isSingaporeNumber(lead.wa_id)}
          onClose={() => setPrices(false)}
          onPick={
            mayWrite
              ? (text) => {
                  setFill({ text, seq: Date.now() });
                  setPrices(false);
                }
              : undefined
          }
        />
      )}
      {/* Over the chat on a narrower computer the chat darkens beside it, and a press there closes it. */}
      {card && !col && <div className={`wc-ovscrim${cardOut ? " out" : ""}`} onClick={() => setDrawer(false)} aria-hidden="true" />}
      {card && (
        <div className={`wc-drawer wc-cd${col && slide ? " in" : ""}${cardOut ? " out" : ""}`}>
          {/* A phone's 客户详情 is a page over the chat and its header: who it is, and ✕ (prototype ChatCustomer's
              drawer). On a computer the card draws the column's own head (进度, 手机, ✕). */}
          <div className="wc-drawer-h">
            <span className="wc-drawer-who">
              <b>{who}</b>
              <small>{isMessenger(lead) ? "Messenger" : `${phoneLabel(lead.wa_id)} · WhatsApp`}</small>
            </span>
            <button type="button" className="wc-ico" onClick={() => setDrawer(false)} title="关掉" aria-label="关掉">
              <ShellIcon name="x" size={20} />
            </button>
          </div>
          <div className="wc-drawer-b">
            <ViewShell wait={<CardWait />}>
              <CustomerCard
                place="chat"
                row={cardRow}
                listed={!!row}
                lead={lead}
                orders={Array.isArray(orders) ? orders.map(brief) : orders}
                editable={mayEdit}
                me={me}
                onProgress={(v) => void changeProgress(v)}
                onNote={saveNote}
                onOrigin={(v) => void changeOrigin(v)}
                onCampaign={(c, pic) => void changeCampaign(c, pic)}
                onReply={mayWrite && !lead.transcript_hidden ? reply : undefined}
                onOpen={onOpen}
                onSale={maySell ? () => setSale(true) : undefined}
                onChanged={onChanged}
                onNumbers={reloadOrders}
                onClose={col ? undefined : () => setDrawer(false)}
              />
            </ViewShell>
          </div>
        </div>
      )}
    </div>
  );
}

// Memo'd: the list beside it changes whenever any chat does, and the open
// chat — every message in it — was drawn again with it.
export default memo(LeadDetail);

/**
 * 小爱回复 as 小爱 + 回复, so the narrowest phones can drop 回复 (chat.css,
 * .wc-rw): at 360 px the header's switch left the customer's name 「A…」.
 */
function Mode({ w }: { w: string }) {
  // One span: as two pieces, the button's flex gap opened between 小爱 and 回复.
  return w.endsWith("回复") ? (
    <span>
      {w.slice(0, -2)}
      <span className="wc-rw">回复</span>
    </span>
  ) : (
    <span>{w}</span>
  );
}
